// ✅ ChatInput.jsx — Mobile-safe voice via MediaRecorder + backend /stt/transcribe
// ✅ Keeps your existing UX: tap mic -> shows wave -> tap ✓ to transcribe+append -> repeat to keep adding
// ✅ No browser SpeechRecognition (removes mobile permission popups + dropped sentences behavior)
// ✅ Fast Chat attachments stay LOCAL after selection and are handed upward only when Send is pressed
// ✅ Attachment-only sends preserve an honest empty text value; no synthetic user prompt is created
// ✅ Focused Document Pages + Ask-this-document context render inside the composer
// ✅ Any armed composer context (follow-up / page focus / document context) clears immediately when Send is accepted
// ✅ Send keeps a subtle professional upward motion with no flame effect

import React, { useState, useRef, useEffect, useLayoutEffect, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaPlus, FaTimes, FaCheck } from "react-icons/fa";
import { FiMic, FiArrowUp, FiChevronDown, FiChevronUp, FiCheck, FiFileText } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useAuth, AuthContext } from "../context/AuthContext";
import { useQuota } from "../context/QuotaContext";
import AttachmentMenu from "./AttachmentMenu";
import AttachmentController from "./AttachmentController";
import FileIcon from "../components/FileIcon"; // ← NEW: use your public/assets icons

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Free-plan UX hint only. The backend remains authoritative for entitlement
// and performs the real 5-question reservation during attachment /commit.
const FREE_DOCUMENT_QUESTION_COST = 5;

// ✅ Composer sizing contract:
// - Stay compact for normal one/two-line input.
// - Grow only after the content no longer fits the default height.
// - Cap at 120px, then scroll internally.
// - Always reset to the compact default after a successful send.
const TEXTAREA_DEFAULT_HEIGHT = 48;
const TEXTAREA_MAX_HEIGHT = 120;
const TEXTAREA_GROW_TOLERANCE_PX = 2;

const normalizeChatMode = (value) =>
  String(value || "").trim().toLowerCase() === "research" ? "research" : "auto";

const normalizeComposerDocumentPageScope = (value) => {
  const source =
    value && typeof value === "object"
      ? value
      : null;

  if (!source) return null;

  const asPage = (raw) => {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return null;
    const page = Math.floor(parsed);
    return page > 0 ? page : null;
  };

  const listedPages = (
    Array.isArray(source.pageNumbers)
      ? source.pageNumbers
      : Array.isArray(source.page_numbers)
      ? source.page_numbers
      : Array.isArray(source.pages)
      ? source.pages
      : []
  )
    .map(asPage)
    .filter(Boolean)
    .sort((a, b) => a - b);

  let startPage =
    asPage(source.startPage) ||
    asPage(source.start_page) ||
    asPage(source.pageStart) ||
    asPage(source.page_start) ||
    asPage(source.page) ||
    listedPages[0] ||
    null;

  let endPage =
    asPage(source.endPage) ||
    asPage(source.end_page) ||
    asPage(source.pageEnd) ||
    asPage(source.page_end) ||
    listedPages[listedPages.length - 1] ||
    startPage;

  if (!startPage) return null;

  if (endPage && endPage < startPage) {
    [startPage, endPage] = [endPage, startPage];
  }

  const defaultLabel =
    startPage === endPage
      ? `Page ${startPage}`
      : `Pages ${startPage}–${endPage}`;

  return {
    startPage,
    endPage: endPage || startPage,
    rangeLabel: defaultLabel,
    label: String(
      source.label ||
        source.title ||
        source.heading ||
        ""
    )
      .replace(/\s+/g, " ")
      .trim(),
  };
};

const getAttachmentTypeLabel = (attachment) => {
  const explicit = String(attachment?.fileTypeLabel || "").trim();
  if (explicit) return explicit.toUpperCase();

  const extension = String(attachment?.fileExtension || "").trim();
  if (extension) return extension.toUpperCase();

  const fileName = String(attachment?.file?.name || "");
  const match = fileName.match(/\.([A-Za-z0-9]+)$/);
  if (match?.[1]) return match[1].toUpperCase();

  const mime = String(
    attachment?.mime || attachment?.file?.type || ""
  ).toLowerCase();

  if (mime === "application/pdf") return "PDF";
  if (mime.includes("wordprocessingml")) return "DOCX";
  if (mime === "application/msword") return "DOC";
  if (mime.includes("spreadsheetml")) return "XLSX";
  if (mime === "application/vnd.ms-excel") return "XLS";
  if (mime === "text/csv") return "CSV";
  if (mime === "text/plain") return "TXT";
  if (mime === "image/jpeg") return "JPEG";
  if (mime === "image/png") return "PNG";
  if (mime === "image/webp") return "WEBP";

  return "FILE";
};

const formatAttachmentSize = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return "";

  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ChatInput = ({
  activeChat,
  userEmail,
  userType,
  sessionId,
  onSend,
  onHeightChange,
  inputRef,
  mode,
  onModeChange,
  pendingFollowUp,
  onCancelFollowUp,
  documentPageScope,
  onClearDocumentPageScope,
  pendingDocumentContext,
  onClearDocumentContext,
  onOpenChatTranscript, // ✅ Pass-through only: ChatInterface owns transcript export/viewer logic
  /** ⬇️ Optional: pass the same width your chat thread uses (e.g., 800) */
  maxWidth,
}) => {
  const safeOnSend = onSend || (() => {});
  const safeOnHeightChange = onHeightChange || (() => {});
  const safeOnModeChange = onModeChange || (() => {});
  const safeOnCancelFollowUp = onCancelFollowUp || (() => {});
  const safeOnClearDocumentPageScope =
    onClearDocumentPageScope || (() => {});
  const safeOnClearDocumentContext =
    onClearDocumentContext || (() => {});
  const { t } = useTranslation();
  const { currentUser, userType: ctxUserType, billing } = useContext(AuthContext);
  const resolvedUserType = userType ?? ctxUserType ?? undefined;
  const resolvedEmail = userEmail ?? currentUser?.email ?? undefined;
  console.log("[ChatInput] resolvedEmail:", resolvedEmail, "resolvedUserType:", resolvedUserType);

  const [input, setInput] = useState("");
  // Fallback state keeps this component usable outside ChatInterface.
  // When ChatInterface supplies `mode`, that controlled value remains authoritative.
  const [localMode, setLocalMode] = useState("auto");
  const selectedMode = normalizeChatMode(mode ?? localMode);
  const followUpQuestion = String(pendingFollowUp?.question || "").trim();
  const focusedDocumentPages =
    normalizeComposerDocumentPageScope(documentPageScope);

  const pendingDocument =
    pendingDocumentContext?.document &&
    typeof pendingDocumentContext.document === "object"
      ? pendingDocumentContext.document
      : pendingDocumentContext &&
        typeof pendingDocumentContext === "object"
      ? pendingDocumentContext
      : null;

  const pendingDocumentName = String(
    pendingDocument?.name ||
      pendingDocument?.fileName ||
      pendingDocument?.file_name ||
      ""
  )
    .replace(/\s+/g, " ")
    .trim();

  // A selected page/section is the more specific document context. Avoid
  // stacking a broad document card and a page-focus card at the same time.
  const showPendingDocumentContext =
    !!pendingDocumentName && !focusedDocumentPages;

  const [recording, setRecording] = useState(false); // recording in progress
  const [voiceReady, setVoiceReady] = useState(false); // audio blob ready (stopped)
  const [voiceText, setVoiceText] = useState(""); // transcript preview (after transcribe)
  const [waveformData, setWaveformData] = useState([]);
  const [isLaunching, setIsLaunching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [draftAttachment, setDraftAttachment] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // NEW: Transcribe state (spinner/guard)
  const [isTranscribing, setIsTranscribing] = useState(false);

  // ✅ NEW: "accept pipeline" busy (covers stop->upload->transcribe so spinner shows on all devices)
  const [isAcceptingVoice, setIsAcceptingVoice] = useState(false);

  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [modeMenuPosition, setModeMenuPosition] = useState({ top: 0, left: 0 });
  const plusButtonRef = useRef(null);
  const modeButtonRef = useRef(null);
  const modeMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const isFlatRef = useRef(false);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const messageCount = useRef(0);
  const hasUserSentMessage = useRef(false);

  // ✅ Mobile/tablet detector (reactive)
  const getIsMobileOrTablet = () => {
    try {
      return typeof window !== "undefined" ? window.innerWidth <= 1024 : false;
    } catch {
      return false;
    }
  };
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(getIsMobileOrTablet);

  useEffect(() => {
    const onResize = () => setIsMobileOrTablet(getIsMobileOrTablet());
    try {
      window.addEventListener("resize", onResize);
    } catch {}
    try {
      window.visualViewport?.addEventListener?.("resize", onResize);
    } catch {}
    return () => {
      try {
        window.removeEventListener("resize", onResize);
      } catch {}
      try {
        window.visualViewport?.removeEventListener?.("resize", onResize);
      } catch {}
    };
  }, []);

  // ✅ Lightweight Auto / Research mode control.
  // Uses a custom portal-based DROP-UP so mobile browsers cannot replace it with
  // a native select sheet/dropdown, and so its stacking is not trapped by composer parents.
  const positionModeMenu = useCallback(() => {
    const anchorEl = modeButtonRef.current;
    if (!anchorEl || typeof window === "undefined") return;

    try {
      const rect = anchorEl.getBoundingClientRect();
      const vv = window.visualViewport;
      const viewportLeft = vv?.offsetLeft || 0;
      const viewportTop = vv?.offsetTop || 0;
      const viewportWidth = vv?.width || window.innerWidth || 0;

      const menuWidth = modeMenuRef.current?.offsetWidth || 264;
      const menuHeight = modeMenuRef.current?.offsetHeight || 132;
      const gap = 8;
      const edge = 8;

      // Always open upward. Clamp horizontally to the currently visible viewport.
      const maxLeft = Math.max(viewportLeft + edge, viewportLeft + viewportWidth - menuWidth - edge);
      const left = Math.min(Math.max(rect.left, viewportLeft + edge), maxLeft);
      const top = Math.max(viewportTop + edge, rect.top - menuHeight - gap);

      setModeMenuPosition({ top: Math.round(top), left: Math.round(left) });
    } catch {}
  }, []);

  useLayoutEffect(() => {
    if (!showModeMenu) return;
    positionModeMenu();

    // Re-run once the portal has measured its real height.
    const raf = requestAnimationFrame(positionModeMenu);
    return () => cancelAnimationFrame(raf);
  }, [showModeMenu, positionModeMenu]);

  useEffect(() => {
    if (!showModeMenu) return;

    const onPointerDown = (event) => {
      const target = event?.target;
      if (modeButtonRef.current?.contains?.(target)) return;
      if (modeMenuRef.current?.contains?.(target)) return;
      setShowModeMenu(false);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowModeMenu(false);
        requestAnimationFrame(() => modeButtonRef.current?.focus?.());
      }
    };

    const onViewportChange = () => positionModeMenu();

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    window.visualViewport?.addEventListener?.("resize", onViewportChange);
    window.visualViewport?.addEventListener?.("scroll", onViewportChange);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
      window.visualViewport?.removeEventListener?.("resize", onViewportChange);
      window.visualViewport?.removeEventListener?.("scroll", onViewportChange);
    };
  }, [showModeMenu, positionModeMenu]);

  const chooseMode = useCallback(
    (nextValue) => {
      const nextMode = normalizeChatMode(nextValue);
      setLocalMode(nextMode);
      safeOnModeChange(nextMode);
      setShowModeMenu(false);
      requestAnimationFrame(() => modeButtonRef.current?.focus?.());
    },
    [safeOnModeChange]
  );

  const resetTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    try {
      el.style.height = `${TEXTAREA_DEFAULT_HEIGHT}px`;
      el.style.overflowY = "hidden";
    } catch {}
  }, []);

  const sizeTextareaToContent = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    try {
      // Always measure from the compact baseline. This prevents a short message from
      // inheriting a previously enlarged height and prevents false "manual resize" growth.
      el.style.height = `${TEXTAREA_DEFAULT_HEIGHT}px`;
      el.style.overflowY = "hidden";

      // Empty input is always the exact compact/default height.
      if (!String(el.value || "").length) return;

      const neededHeight = Math.max(TEXTAREA_DEFAULT_HEIGHT, el.scrollHeight || 0);
      const shouldGrow = neededHeight > TEXTAREA_DEFAULT_HEIGHT + TEXTAREA_GROW_TOLERANCE_PX;
      const nextHeight = shouldGrow
        ? Math.min(neededHeight, TEXTAREA_MAX_HEIGHT)
        : TEXTAREA_DEFAULT_HEIGHT;

      el.style.height = `${nextHeight}px`;
      el.style.overflowY = neededHeight > TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
    } catch (e) {
      console.warn("[ChatInput] textarea auto-grow failed:", e);
    }
  }, []);

  // ✅ Compact-first auto-grow.
  // A normal one- or two-line entry stays at the baseline height. Growth occurs only
  // when the rendered content actually exceeds that baseline, then shrinks again when
  // content is deleted. Empty input always returns to the default height.
  useLayoutEffect(() => {
    sizeTextareaToContent();
  }, [input, isMobileOrTablet, sizeTextareaToContent]);

  // ✅ Give ChatInterface a real reference to this exact textarea.
  // This makes the follow-up chevron -> composer focus path deterministic.
  useLayoutEffect(() => {
    const node = textareaRef.current;
    try {
      if (typeof inputRef === "function") {
        inputRef(node);
      } else if (inputRef && typeof inputRef === "object") {
        inputRef.current = node;
      }
    } catch {}

    return () => {
      try {
        if (typeof inputRef === "function") {
          inputRef(null);
        } else if (inputRef && typeof inputRef === "object" && inputRef.current === node) {
          inputRef.current = null;
        }
      } catch {}
    };
  }, [inputRef]);

  // ✅ Focus the answer field whenever ChatInterface arms an open-text follow-up.
  useEffect(() => {
    if (!followUpQuestion) return;

    const focus = () => {
      try {
        textareaRef.current?.focus?.();
      } catch {}
    };

    try {
      requestAnimationFrame(focus);
    } catch {
      focus();
    }
    const timer = setTimeout(focus, 80);
    return () => clearTimeout(timer);
  }, [followUpQuestion]);

  // Compatibility bridge for ChatInterface's explicit focus event.
  useEffect(() => {
    const onFocusChatInput = () => {
      try {
        textareaRef.current?.focus?.();
      } catch {}
    };

    window.addEventListener("asklawly_focus_chat_input", onFocusChatInput);
    return () => window.removeEventListener("asklawly_focus_chat_input", onFocusChatInput);
  }, []);

  // ✅ Inject keyframes ONCE (prevents spinner "flicker/jitter" caused by frequent re-renders)
  useEffect(() => {
    const STYLE_ID = "asklawly-chatinput-keyframes";
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.type = "text/css";
    style.textContent = `
@keyframes asklawlySendLift {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
@keyframes asklawlySpin360 {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
    document.head.appendChild(style);
  }, []);

  // ✅ Mode control styling kept separate from keyframes so hot reloads cannot leave
  // an older style element behind. Default is transparent; hover/focus is subtle gray.
  useEffect(() => {
    const STYLE_ID = "asklawly-chatinput-mode-style";
    if (typeof document === "undefined") return;

    let style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ID;
      style.type = "text/css";
      document.head.appendChild(style);
    }

    style.textContent = `
      .asklawly-mode-trigger {
        appearance: none;
        -webkit-appearance: none;
        border: 0;
        background: transparent;
        color: #4b5563;
        border-radius: 10px;
        min-height: 36px;
        padding: 7px 8px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font: 500 14px/1.2 system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        cursor: pointer;
        transition: background-color 120ms ease, color 120ms ease;
        -webkit-tap-highlight-color: transparent;
      }
      .asklawly-mode-trigger:hover,
      .asklawly-mode-trigger:focus-visible,
      .asklawly-mode-trigger[data-open="true"] {
        background: #f3f4f6;
        color: #111827;
        outline: none;
      }
      .asklawly-mode-trigger:disabled {
        opacity: .5;
        cursor: not-allowed;
      }
      .asklawly-mode-menu-item {
        width: 100%;
        border: 0;
        background: transparent;
        border-radius: 10px;
        padding: 9px 10px;
        display: flex;
        align-items: flex-start;
        gap: 9px;
        text-align: left;
        cursor: pointer;
        color: #111827;
        -webkit-tap-highlight-color: transparent;
      }
      .asklawly-mode-menu-item:hover,
      .asklawly-mode-menu-item:focus-visible {
        background: #f3f4f6;
        outline: none;
      }
    `;
  }, []);

  // ✅ Robust tap guard (prevents double-trigger on mobile)
  const lastVoiceActionAtRef = useRef(0);
  const safeVoiceAction = (fn) => {
    const now = Date.now();
    if (now - (lastVoiceActionAtRef.current || 0) < 250) return;
    lastVoiceActionAtRef.current = now;
    fn?.();
  };

  // ✅ Guard to prevent annoying repeated alerts when not logged in
  const lastAuthAlertAtRef = useRef(0);
  const showAuthAlertOnce = (msg) => {
    const now = Date.now();
    if (now - (lastAuthAlertAtRef.current || 0) < 1500) return false; // 1.5s cooldown
    lastAuthAlertAtRef.current = now;
    alert(msg);
    return true;
  };

  const {
    upgradeMessage: authUpgradeMessage,
    showUpgrade: authShowUpgrade,
    chatInputDisabled: authChatInputDisabled,
  } = useAuth();

  const {
    upgradeMessage: quotaUpgradeMessage,
    showUpgrade: quotaShowUpgrade,
    chatInputDisabled: quotaChatInputDisabled,
  } = useQuota();

  const finalUpgradeMessage = authUpgradeMessage || quotaUpgradeMessage;
  const finalShowUpgrade = authShowUpgrade || quotaShowUpgrade;
  const finalChatInputDisabled = authChatInputDisabled || quotaChatInputDisabled;

  // Frontend-only convenience guard for NEW Free-plan document ingestion.
  // Do not expose the exact remaining balance here, and do not treat this as
  // billing authority: /reserve + /commit still make the authoritative decision.
  const billingPlanType = String(
    billing?.raw_plan_type ||
      billing?.plan_type ||
      ""
  )
    .trim()
    .toLowerCase();

  const isFreePlan = billingPlanType === "free";
  const newFreeDocumentSelected = !!(
    isFreePlan &&
    draftAttachment?.pendingUpload
  );

  const locallyKnownRemainingQuestions =
    billing?.remaining_questions !== null &&
    billing?.remaining_questions !== undefined &&
    String(billing.remaining_questions).trim() !== ""
      ? Number(billing.remaining_questions)
      : Number.NaN;

  const hasLocallyKnownFreeBalance = !!(
    isFreePlan &&
    Number.isFinite(locallyKnownRemainingQuestions)
  );

  const freeDocumentQuotaInsufficient = !!(
    newFreeDocumentSelected &&
    hasLocallyKnownFreeBalance &&
    locallyKnownRemainingQuestions < FREE_DOCUMENT_QUESTION_COST
  );

  // ✅ Wave animation ONLY while recording or when blob ready.
  // ❗️Do NOT run interval during isTranscribing / isAcceptingVoice — it causes frequent re-renders that can make spinner look like flicker/wiggle.
  useEffect(() => {
    if (isTranscribing || isAcceptingVoice) return; // ✅ freeze waveform updates while busy (prevents spinner jitter)
    if (!recording && !voiceReady) return;

    const interval = setInterval(() => {
      const sample = isFlatRef.current ? 2 : Math.random() * 20;
      setWaveformData((data) => [...data.slice(-100), sample]);
    }, 60);

    return () => clearInterval(interval);
  }, [recording, voiceReady, isTranscribing, isAcceptingVoice]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      safeOnHeightChange(entry.contentRect.height);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // ✅ listen for ChatInterface draft injection (copy/edit icons)
  useEffect(() => {
    const onDraft = (e) => {
      try {
        const txt = (e?.detail?.text || "").toString();
        const s = txt.trim();
        if (!s) return;

        setInput(s);

        requestAnimationFrame(() => {
          try {
            textareaRef.current?.focus?.();
            const el = textareaRef.current;
            if (el && typeof el.selectionStart === "number") {
              const end = el.value.length;
              el.selectionStart = end;
              el.selectionEnd = end;
            }
          } catch {}
        });
      } catch {}
    };

    window.addEventListener("asklawly_chat_draft", onDraft);
    return () => window.removeEventListener("asklawly_chat_draft", onDraft);
  }, []);

  // ----------------------------
  // MediaRecorder refs/state
  // ----------------------------
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);

  const recordedBlobRef = useRef(null);
  const recordedMimeRef = useRef("");
  const pendingAcceptRef = useRef(false); // if user taps ✓ while recording, we stop and then transcribe

  // ✅ Minimums to avoid "empty_transcript" from too-short / too-tiny recordings
  const MIN_RECORD_MS = 1000; // 1.0s minimum
  const MIN_BLOB_BYTES = 15000; // ~15KB minimum (tuned for opus/webm)
  const recordStartAtRef = useRef(0);

  // NEW: Ensure stop() flushes last chunk before transcribe (prevents empty_transcript)
  const stopPromiseRef = useRef(null);
  const ensureRecorderStopped = () => {
    try {
      const mr = mediaRecorderRef.current;
      if (!mr) return Promise.resolve();
      if (mr.state === "inactive") return Promise.resolve();
      if (stopPromiseRef.current) return stopPromiseRef.current;

      stopPromiseRef.current = new Promise((resolve) => {
        const prevOnStop = mr.onstop;
        mr.onstop = (ev) => {
          try {
            prevOnStop?.(ev);
          } catch {}
          resolve();
          stopPromiseRef.current = null;
        };

        // Flush final buffers before stop (some browsers need this)
        try {
          mr.requestData?.();
        } catch {}

        try {
          mr.stop();
        } catch {
          resolve();
          stopPromiseRef.current = null;
        }
      });

      return stopPromiseRef.current;
    } catch {
      return Promise.resolve();
    }
  };

  // --- Text helpers (keep yours; used to append multiple transcriptions cleanly) ---
  const normalizeSpaces = (s = "") => s.replace(/\s+/g, " ").trim();

  const needsSpaceBetween = (left = "", right = "") => {
    if (!left || !right) return false;
    const l = left.slice(-1);
    const r = right[0];
    if (/^[,.;:!?)]$/.test(r)) return false;
    if (!/\s/.test(l) && !/[([{-]/.test(l)) return true;
    return false;
  };

  const appendWithDeoverlap = (base = "", next = "") => {
    let a = normalizeSpaces(base);
    let b = normalizeSpaces(next);
    if (!b) return a;

    if (a.toLowerCase().endsWith(b.toLowerCase())) return a;

    const maxCheck = Math.min(50, a.length, b.length);
    for (let k = maxCheck; k > 0; k--) {
      if (a.slice(-k).toLowerCase() === b.slice(0, k).toLowerCase()) {
        b = b.slice(k);
        break;
      }
    }

    if (!b) return a;
    const glue = needsSpaceBetween(a, b) ? " " : "";
    return (a + glue + b).trim();
  };
  // --- end helpers ---

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        mediaRecorderRef.current?.stop?.();
      } catch {}
      mediaRecorderRef.current = null;

      try {
        streamRef.current?.getTracks?.().forEach((t) => {
          try {
            t.stop();
          } catch {}
        });
      } catch {}
      streamRef.current = null;

      recordedChunksRef.current = [];
      recordedBlobRef.current = null;
      recordedMimeRef.current = "";
      pendingAcceptRef.current = false;
      stopPromiseRef.current = null;
      recordStartAtRef.current = 0;

      // ✅ ensure spinners reset
      try {
        setIsAcceptingVoice(false);
        setIsTranscribing(false);
      } catch {}
    };
  }, []);

  // ✅ Send transaction handoff:
  // - Text-only: pass the text upward as before.
  // - Fast Chat attachment: keep the ORIGINAL local File + uploadOnSend callback
  //   intact and hand them to ChatInterface. ChatInput itself does NOT upload.
  // - Attachment-only: text remains an honest empty string; no synthetic user text.
  const handleSend = async () => {
    if (isUploading) return;
    if (isSending) return;

    const textOnly = input.trim();
    const hasAttachment = !!draftAttachment;

    if ((!textOnly && !hasAttachment) || finalChatInputDisabled) return;

    // Immediate UX guard only. The backend still re-checks current billing
    // during /reserve and establishes the real 5-question hold during /commit.
    if (freeDocumentQuotaInsufficient) return;

    const attachmentPayload = hasAttachment
      ? {
          // Existing/legacy attachment metadata remains available for compatibility.
          fileUrl: draftAttachment.fileUrl || null,
          fileUrls:
            draftAttachment.fileUrls ||
            (draftAttachment.fileUrl ? [draftAttachment.fileUrl] : []),
          previewUrl: draftAttachment.previewUrl || null,
          fileName: draftAttachment.file?.name || null,
          mime:
            draftAttachment.file?.type ||
            draftAttachment.mime ||
            "",
          sizeBytes:
            Number.isFinite(Number(draftAttachment.sizeBytes))
              ? Number(draftAttachment.sizeBytes)
              : Number.isFinite(Number(draftAttachment.file?.size))
              ? Number(draftAttachment.file.size)
              : null,
          frameIds: Array.isArray(draftAttachment.frameIds)
            ? draftAttachment.frameIds
            : [],
          uploadBatchId: draftAttachment.uploadBatchId || null,
          sessionKind: "fast_chat",

          // Fast Chat / Document Intelligence send-trigger contract.
          // These are intentionally NOT serialized here. They move directly from
          // ChatInput -> ChatInterface in browser memory.
          localFile: draftAttachment.localFile || draftAttachment.file || null,
          pendingUpload: !!draftAttachment.pendingUpload,
          uploadOnSend:
            typeof draftAttachment.uploadOnSend === "function"
              ? draftAttachment.uploadOnSend
              : null,

          // If this attachment has already been processed/reused, preserve its
          // authoritative DI identity/metadata too.
          documentId: draftAttachment.documentId || null,
          documentStatus: draftAttachment.documentStatus || null,
          documentType: draftAttachment.documentType || null,
          documentTypeConfidence:
            draftAttachment.documentTypeConfidence ?? null,
          pageCount:
            Number.isFinite(Number(draftAttachment.pageCount))
              ? Number(draftAttachment.pageCount)
              : null,
          workerRegion: draftAttachment.workerRegion || null,
          manifestPath: draftAttachment.manifestPath || null,
          briefPath: draftAttachment.briefPath || null,
          pageMapPath: draftAttachment.pageMapPath || null,
          indexManifestPath: draftAttachment.indexManifestPath || null,
          warningCount:
            Number.isFinite(Number(draftAttachment.warningCount))
              ? Number(draftAttachment.warningCount)
              : 0,
          attachmentMode: draftAttachment.mode || null,
        }
      : null;

    // Preserve exactly what the user typed.
    // Attachment-only sends are valid and intentionally use text="".
    const textToSend = textOnly;
    const savedDraft = draftAttachment;

    const followUpForSend = followUpQuestion
      ? {
          ...(pendingFollowUp || {}),
          question: followUpQuestion,
        }
      : null;

    const consumeFollowUpContext = !!followUpForSend;
    const consumeDocumentPageScope = !!focusedDocumentPages;
    const consumeDocumentContext = !!pendingDocumentName;

    setInput("");
    setDraftAttachment(null);

    // Composer context is one-turn state. Clear it immediately when Send is
    // accepted instead of leaving stale cards visible while Lexa is answering.
    if (consumeFollowUpContext) {
      safeOnCancelFollowUp();
    }
    if (consumeDocumentPageScope) {
      safeOnClearDocumentPageScope();
    }
    if (consumeDocumentContext) {
      safeOnClearDocumentContext();
    }

    // Reset synchronously so the composer drops back to its exact compact size
    // immediately after Send, rather than waiting for the next render/effect.
    resetTextareaHeight();

    setIsSending(true);
    setIsLaunching(true);

    try {
      await safeOnSend({
        text: textToSend,
        attachment: attachmentPayload,
        mode: selectedMode,
        ...(followUpForSend
          ? {
              message_type: "follow_up_answer",
              follow_up: followUpForSend,
            }
          : {}),
      });

      // Revoke local presentation URLs only after the entire parent Send
      // transaction succeeds. If it fails, the draft is restored below.
      if (savedDraft?.previewUrl) {
        URL.revokeObjectURL(savedDraft.previewUrl);
      }
      if (savedDraft?.imagePreviewUrl) {
        URL.revokeObjectURL(savedDraft.imagePreviewUrl);
      }

      messageCount.current += 1;
      if (messageCount.current === 2) {
        window.dispatchEvent(new Event("triggerRefreshSidebar"));
      }
      if (!hasUserSentMessage.current) {
        hasUserSentMessage.current = true;
        window.dispatchEvent(new Event("userHasSentMessage"));
      }
    } catch (err) {
      console.error("Send failed:", err);
      alert("Failed to send. Please try again.");

      setInput((prev) => prev || textOnly);
      if (savedDraft) setDraftAttachment(savedDraft);
    } finally {
      setIsSending(false);
      setTimeout(() => setIsLaunching(false), 300);
    }
  };

  // ----------------------------
  // Voice: MediaRecorder + backend STT
  // ----------------------------
  const pickBestMimeType = () => {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
    for (const t of candidates) {
      try {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t;
      } catch {}
    }
    return ""; // let browser decide
  };

  const stopTracks = (stream) => {
    try {
      stream?.getTracks?.().forEach((t) => {
        try {
          t.stop();
        } catch {}
      });
    } catch {}
  };

  const resetVoiceState = () => {
    setRecording(false);
    setVoiceReady(false);
    setVoiceText("");
    setWaveformData([]);
    isFlatRef.current = true;

    recordedChunksRef.current = [];
    recordedBlobRef.current = null;
    recordedMimeRef.current = "";
    pendingAcceptRef.current = false;
    stopPromiseRef.current = null;
    recordStartAtRef.current = 0;

    // ✅ clear busy flags
    setIsAcceptingVoice(false);
  };

  const startRecording = async () => {
    if (isTranscribing || isAcceptingVoice) return;
    if (recording) return;

    if (!navigator?.mediaDevices?.getUserMedia) {
      alert("Microphone not supported on this device/browser.");
      return;
    }

    recordedChunksRef.current = [];
    recordedBlobRef.current = null;
    recordedMimeRef.current = "";
    setVoiceReady(false);
    setVoiceText("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const mimeType = pickBestMimeType();
      recordedMimeRef.current = mimeType || "";

      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e?.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      mr.onstart = () => {
        recordStartAtRef.current = Date.now();
        setRecording(true);
        isFlatRef.current = false;
      };

      mr.onstop = () => {
        setRecording(false);
        isFlatRef.current = true;

        const chunks = recordedChunksRef.current || [];
        const type = recordedMimeRef.current || chunks?.[0]?.type || "audio/webm";
        const blob = new Blob(chunks, { type });
        recordedBlobRef.current = blob;

        // Stop mic hardware immediately after recording
        try {
          stopTracks(streamRef.current);
        } catch {}
        streamRef.current = null;

        // Guard: too short OR too small blobs cause empty transcripts
        const durMs = recordStartAtRef.current ? Date.now() - recordStartAtRef.current : 0;

        console.log("[STT][client] onstop:", {
          durMs,
          size: blob?.size || 0,
          mime: type,
          chunks: chunks?.length || 0,
        });

        if (!blob || blob.size <= 0) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          alert("No audio captured. Please try again and speak a little longer.");
          setIsAcceptingVoice(false);
          return;
        }

        if (durMs > 0 && durMs < MIN_RECORD_MS) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          alert("Recording too short. Please speak for at least 1 second and try again.");
          setIsAcceptingVoice(false);
          return;
        }

        if (blob.size < MIN_BLOB_BYTES) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          alert("Audio too quiet/short. Please speak a bit longer and try again.");
          setIsAcceptingVoice(false);
          return;
        }

        setVoiceReady(true);

        // If user hit ✓ while still recording, transcribe immediately now
        if (pendingAcceptRef.current) {
          pendingAcceptRef.current = false;
          transcribeAndAppend(blob, type);
        }
      };

      // timeslice keeps data flowing; helps some mobile browsers
      mr.start(250);
    } catch (err) {
      console.warn("Mic start error:", err);
      resetVoiceState();
      alert("Microphone access is blocked or unavailable. Please allow microphone permission and try again.");
    }
  };

  const stopRecording = async () => {
    try {
      // extra flush + tiny delay helps some browsers finalize last chunk
      try {
        mediaRecorderRef.current?.requestData?.();
      } catch {}
      await new Promise((r) => setTimeout(r, 180));
      await ensureRecorderStopped();
    } catch (e) {
      console.warn("stopRecording failed:", e);
    }
  };

  const cancelVoice = () => {
    safeVoiceAction(() => {
      if (isTranscribing) return;

      try {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") {
          mr.ondataavailable = null;
          mr.onstop = null;
          mr.stop();
        }
      } catch {}

      mediaRecorderRef.current = null;

      try {
        stopTracks(streamRef.current);
      } catch {}
      streamRef.current = null;

      setIsAcceptingVoice(false);
      resetVoiceState();
    });
  };

  const getIdTokenSafe = async () => {
    try {
      if (!currentUser?.getIdToken) return null;
      return await currentUser.getIdToken();
    } catch (e) {
      console.warn("getIdToken failed:", e);
      return null;
    }
  };

  const transcribeAndAppend = async (blob, mimeType) => {
    if (!HOST) {
      alert("Backend URL not configured (REACT_APP_BACKEND_URL).");
      setIsAcceptingVoice(false);
      return;
    }
    if (!blob || blob.size <= 0) {
      alert("No audio captured. Try again.");
      setIsAcceptingVoice(false);
      return;
    }

    // Guard: enforce the same minimums here too (in case user retries)
    if (blob.size < MIN_BLOB_BYTES) {
      alert("Audio too quiet/short. Please speak a bit longer and try again.");
      setIsAcceptingVoice(false);
      return;
    }

    const token = await getIdTokenSafe();
    if (!token) {
      // ✅ prevent repeated alert spam / "stuck" behavior
      showAuthAlertOnce("Please log in again to use voice input.");
      setIsAcceptingVoice(false);
      return;
    }

    setIsTranscribing(true);
    setVoiceText("");

    try {
      const ext = (mimeType || "").includes("mp4")
        ? "m4a"
        : (mimeType || "").includes("mpeg")
        ? "mp3"
        : "webm";

      const file = new File([blob], `speech.${ext}`, { type: mimeType || "audio/webm" });

      const fd = new FormData();
      fd.append("file", file);

      // ✅ language autodetect: send "auto" (or omit, but we'll keep explicit)
      fd.append("language", "auto");

      fd.append("sessionId", sessionId || "");
      fd.append("sessionKind", "fast_chat");
      fd.append("userEmail", resolvedEmail || "");
      fd.append("userType", resolvedUserType || "");

      console.log("[STT][client] uploading:", {
        url: `${HOST}/stt/transcribe`,
        size: blob.size,
        mime: mimeType || "audio/webm",
        filename: `speech.${ext}`,
      });

      const res = await fetch(`${HOST}/stt/transcribe`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        const reason = data?.reason || data?.error || `stt_failed_${res.status}`;
        throw new Error(reason);
      }

      const text = (data?.text || data?.transcript || "").toString().trim();
      if (!text) {
        throw new Error("empty_transcript");
      }

      setVoiceText(text);

      setInput((prev) => {
        const left = normalizeSpaces(prev || "");
        if (!left) return text;
        return appendWithDeoverlap(left, text);
      });

      requestAnimationFrame(() => {
        try {
          const el = textareaRef.current;
          el?.focus?.();
          if (el && typeof el.selectionStart === "number") {
            const end = (el.value || "").length;
            el.selectionStart = end;
            el.selectionEnd = end;
          }
        } catch {}
      });

      resetVoiceState();
    } catch (err) {
      console.error("Transcribe failed:", err);
      alert(`Voice transcription failed (${String(err?.message || err)}). Please try again.`);
      setVoiceReady(true);
    } finally {
      setIsTranscribing(false);
      setIsAcceptingVoice(false);
    }
  };

  // Tap mic
  const handleVoiceInput = () => {
    safeVoiceAction(() => {
      if (finalChatInputDisabled) return;
      if (isUploading) return;
      if (isSending) return;

      // ✅ If not logged in, show once and stop (prevents re-trigger loop)
      if (!currentUser) {
        showAuthAlertOnce("Please log in again to use voice input.");
        return;
      }

      if (recording) return;
      if (isTranscribing || isAcceptingVoice) return;

      if (voiceReady) {
        resetVoiceState();
      }

      startRecording();
    });
  };

  // Tap ✓ : if recording -> stop then auto-transcribe; if blob ready -> transcribe
  const acceptVoice = () => {
    safeVoiceAction(async () => {
      if (isTranscribing || isAcceptingVoice) return;

      // ✅ If not logged in, show once and stop (prevents re-trigger loop)
      if (!currentUser) {
        showAuthAlertOnce("Please log in again to use voice input.");
        return;
      }

      // ✅ Start spinner immediately (covers stop -> onstop -> upload)
      setIsAcceptingVoice(true);

      try {
        if (recording) {
          // Guard: do not allow accept under 1s
          const durMs = recordStartAtRef.current ? Date.now() - recordStartAtRef.current : 0;
          if (durMs > 0 && durMs < MIN_RECORD_MS) {
            alert("Recording too short. Please speak for at least 1 second.");
            setIsAcceptingVoice(false);
            return;
          }

          pendingAcceptRef.current = true;
          await stopRecording();
          return;
        }

        if (voiceReady && recordedBlobRef.current) {
          await transcribeAndAppend(recordedBlobRef.current, recordedMimeRef.current || recordedBlobRef.current.type);
          return;
        }

        // Nothing to do
        setIsAcceptingVoice(false);
      } catch (e) {
        console.warn("acceptVoice failed:", e);
        setIsAcceptingVoice(false);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const containerStyle = {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    padding: isMobileOrTablet ? "0" : "0",
    ...(isMobileOrTablet && {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      background: "#fff",
      zIndex: 2,
    }),
  };

  const effectiveMaxWidth = typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth || "800px";

  const showWave = recording || voiceReady || isTranscribing || isAcceptingVoice;

  // ✅ Clear 360° spinner (SVG) — now Safari/iOS safe
  const Spinner = ({ size = 16 }) => (
    <span
      aria-label="Loading"
      style={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        willChange: "transform",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        style={{
          display: "block",
          // ✅ iOS/Safari needs transformBox for reliable SVG rotation
          transformBox: "fill-box",
          transformOrigin: "50% 50%",
          WebkitTransformOrigin: "50% 50%",
          animation: "asklawlySpin360 0.85s linear infinite",
          WebkitAnimation: "asklawlySpin360 0.85s linear infinite",
        }}
      >
        <circle cx="12" cy="12" r="9" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="3" />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          fill="none"
          stroke="#111827"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );

  return (
    <div ref={containerRef} style={containerStyle}>
      <div style={{ width: "100%", maxWidth: effectiveMaxWidth }}>
        <div
          ref={cardRef}
          style={{
            width: "100%",
            border: "1px solid #ddd",
            borderRadius: 20,
            backgroundColor: "#fff",
            padding: 10,
            display: "flex",
            flexDirection: "column",
            margin: 0,
            boxSizing: "border-box",
          }}
        >
          <div style={{ position: "relative", width: "100%" }}>
            {showWave ? (
              <div
                style={{
                  height: 60,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <div style={{ display: "flex", width: "100%", gap: 2 }}>
                  {waveformData.map((v, i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        height: `${v * 2}px`,
                        backgroundColor: "#555",
                        marginTop: `${10 - v / 2}px`,
                        marginBottom: `${10 - v / 2}px`,
                        borderRadius: 1,
                      }}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <>
                {finalShowUpgrade && (
                  <div
                    style={{
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffeeba",
                      color: "#856404",
                      padding: "10px",
                      marginBottom: "10px",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  >
                    <strong>Notice:</strong> {finalUpgradeMessage}
                  </div>
                )}

                {showPendingDocumentContext && (
                  <div
                    role="status"
                    aria-label="Asking this document"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      padding: "8px 10px",
                      marginBottom: 6,
                      borderRadius: 11,
                      background: "#f7f8fc",
                      border: "1px solid rgba(30, 42, 120, 0.14)",
                      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 28,
                        height: 28,
                        flex: "0 0 28px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 9,
                        color: "#1E2A78",
                        background: "#eef1f8",
                        border: "1px solid rgba(30, 42, 120, 0.09)",
                        fontSize: 14,
                      }}
                    >
                      <FiFileText />
                    </span>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 10.5,
                          lineHeight: 1.2,
                          fontWeight: 750,
                          letterSpacing: "0.025em",
                          textTransform: "uppercase",
                          color: "#667085",
                          marginBottom: 2,
                        }}
                      >
                        Asking This Document
                      </div>

                      <div
                        title={pendingDocumentName}
                        style={{
                          minWidth: 0,
                          fontSize: 13,
                          lineHeight: 1.35,
                          fontWeight: 650,
                          color: "#111827",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {pendingDocumentName}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => safeOnClearDocumentContext()}
                      disabled={isSending || isUploading}
                      aria-label="Clear document context"
                      title="Clear document context"
                      style={{
                        width: 28,
                        height: 28,
                        flex: "0 0 28px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "none",
                        borderRadius: 8,
                        background: "transparent",
                        color: "#667085",
                        cursor:
                          isSending || isUploading
                            ? "default"
                            : "pointer",
                        opacity:
                          isSending || isUploading
                            ? 0.45
                            : 1,
                        padding: 0,
                        fontSize: 14,
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}

                {focusedDocumentPages && (
                  <div
                    role="status"
                    aria-label="Focused document pages"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      padding: "8px 10px",
                      marginBottom: 6,
                      borderRadius: 11,
                      background: "#f7f8fc",
                      border: "1px solid rgba(30, 42, 120, 0.14)",
                      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 28,
                        height: 28,
                        flex: "0 0 28px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 9,
                        color: "#1E2A78",
                        background: "#eef1f8",
                        border: "1px solid rgba(30, 42, 120, 0.09)",
                        fontSize: 14,
                      }}
                    >
                      <FiFileText />
                    </span>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 10.5,
                          lineHeight: 1.2,
                          fontWeight: 750,
                          letterSpacing: "0.025em",
                          textTransform: "uppercase",
                          color: "#667085",
                          marginBottom: 2,
                        }}
                      >
                        Focused Document Pages
                      </div>

                      <div
                        title={
                          focusedDocumentPages.label
                            ? `${focusedDocumentPages.rangeLabel} · ${focusedDocumentPages.label}`
                            : focusedDocumentPages.rangeLabel
                        }
                        style={{
                          minWidth: 0,
                          fontSize: 13,
                          lineHeight: 1.35,
                          fontWeight: 650,
                          color: "#111827",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {focusedDocumentPages.rangeLabel}
                        {focusedDocumentPages.label
                          ? ` · ${focusedDocumentPages.label}`
                          : ""}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        safeOnClearDocumentPageScope()
                      }
                      disabled={isSending || isUploading}
                      aria-label="Clear focused document pages"
                      title="Clear focused pages"
                      style={{
                        width: 28,
                        height: 28,
                        flex: "0 0 28px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "none",
                        borderRadius: 8,
                        background: "transparent",
                        color: "#667085",
                        cursor:
                          isSending || isUploading
                            ? "default"
                            : "pointer",
                        opacity:
                          isSending || isUploading
                            ? 0.45
                            : 1,
                        padding: 0,
                        fontSize: 14,
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}

                {followUpQuestion && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      padding: "8px 10px",
                      marginBottom: 6,
                      borderRadius: 10,
                      background: "#f7f7fb",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#6B7280",
                          marginBottom: 2,
                        }}
                      >
                        Answering Lexa
                      </div>
                      <div
                        title={followUpQuestion}
                        style={{
                          fontSize: 13,
                          lineHeight: 1.35,
                          color: "#111827",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {followUpQuestion}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => safeOnCancelFollowUp()}
                      aria-label="Cancel follow-up answer"
                      title="Cancel reply"
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#6B7280",
                        cursor: "pointer",
                        padding: 2,
                        lineHeight: 1,
                        fontSize: 14,
                        flex: "0 0 auto",
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}

                <textarea
                  ref={textareaRef}
                  disabled={finalChatInputDisabled || isUploading}
                  style={{
                    width: "100%",
                    fontSize: 16,
                    border: "none",
                    outline: "none",

                    // ✅ Desktop keeps corner resize; mobile/tablet disables manual resize.
                    resize: isMobileOrTablet ? "none" : "vertical",

                    // ✅ Explicit compact baseline: one/two-line input does not enlarge the composer.
                    // Height itself is controlled imperatively by the sizing helper so unrelated
                    // React re-renders cannot accidentally collapse a legitimately expanded draft.
                    minHeight: TEXTAREA_DEFAULT_HEIGHT,
                    maxHeight: TEXTAREA_MAX_HEIGHT,
                    overflowY: "hidden",
                    boxSizing: "border-box",
                    padding: "2px 0",

                    lineHeight: 1.4,
                    fontFamily: "inherit",
                  }}
                  rows={2}
                  placeholder={
                    followUpQuestion
                      ? "Type your answer…"
                      : currentUser
                      ? t("Ask your question")
                      : "🔒 Please log in to ask your question"
                  }
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />

                {/* Draft attachment display */}
                {draftAttachment && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 9px",
                      marginTop: 8,
                      width: "fit-content",
                      maxWidth: "100%",
                      minWidth: 0,
                      borderRadius: 12,
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
                      boxSizing: "border-box",
                    }}
                  >
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        flex: "0 0 42px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        borderRadius: 9,
                        background: "#ffffff",
                        border: "1px solid #e5e7eb",
                        boxSizing: "border-box",
                      }}
                    >
                      {draftAttachment.previewUrl ? (
                        <img
                          src={
                            draftAttachment.imagePreviewUrl ||
                            draftAttachment.previewUrl
                          }
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <FileIcon
                          fileName={draftAttachment.file?.name}
                          size={30}
                          alt="File"
                        />
                      )}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: 4,
                        minWidth: 0,
                        flex: "1 1 auto",
                      }}
                    >
                      <span
                        title={draftAttachment.file?.name}
                        style={{
                          display: "block",
                          maxWidth: isMobileOrTablet ? 190 : 300,
                          fontSize: 13,
                          lineHeight: 1.25,
                          fontWeight: 600,
                          color: "#111827",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {draftAttachment.file?.name || "Attachment"}
                      </span>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            minHeight: 18,
                            padding: "1px 6px",
                            borderRadius: 999,
                            background: "#eef2ff",
                            color: "#4338ca",
                            fontSize: 10,
                            lineHeight: 1.2,
                            fontWeight: 700,
                            letterSpacing: "0.03em",
                          }}
                        >
                          {getAttachmentTypeLabel(draftAttachment)}
                        </span>

                        {!!formatAttachmentSize(
                          draftAttachment.sizeBytes ||
                            draftAttachment.file?.size
                        ) && (
                          <span
                            style={{
                              fontSize: 11,
                              lineHeight: 1.2,
                              color: "#6b7280",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatAttachmentSize(
                              draftAttachment.sizeBytes ||
                                draftAttachment.file?.size
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      onPointerDown={() => {
                        if (draftAttachment?.previewUrl) {
                          URL.revokeObjectURL(draftAttachment.previewUrl);
                        }
                        if (draftAttachment?.imagePreviewUrl) {
                          URL.revokeObjectURL(
                            draftAttachment.imagePreviewUrl
                          );
                        }
                        setDraftAttachment(null);
                      }}
                      type="button"
                      aria-label="Remove attachment"
                      title="Remove attachment"
                      style={{
                        width: 28,
                        height: 28,
                        flex: "0 0 28px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #e5e7eb",
                        borderRadius: "50%",
                        background: "#ffffff",
                        color: "#6b7280",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: 12,
                        lineHeight: 1,
                      }}
                    >
                      <FaTimes aria-hidden="true" />
                    </button>
                  </div>
                )}

                {newFreeDocumentSelected && (
                  <div
                    aria-live="polite"
                    style={{
                      marginTop: 6,
                      paddingLeft: 2,
                      paddingRight: 2,
                      maxWidth: "100%",
                      fontSize: 12,
                      lineHeight: 1.35,
                      color: "#6b7280",
                    }}
                  >
                    <div>Document analysis uses 5 Free questions.</div>

                    {freeDocumentQuotaInsufficient && (
                      <div
                        style={{
                          marginTop: 3,
                          color: "#92400e",
                          fontWeight: 600,
                        }}
                      >
                        You don’t have enough Free usage remaining to analyze a new
                        document. Upgrade to continue.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <AttachmentMenu
            open={showAttachmentMenu}
            onClose={() => setShowAttachmentMenu(false)}
            fileInputRef={fileInputRef}
            anchorRef={plusButtonRef}
            onOpenChatTranscript={onOpenChatTranscript}
          />

          {currentUser && (
            <AttachmentController
              fileInputRef={fileInputRef}
              uid={currentUser.uid}
              email={resolvedEmail}
              sessionKind="fast_chat"
              sessionId={sessionId}
              userType={resolvedUserType}
              onUploadingChange={setIsUploading}
              onAttachmentReady={(att) => {
                console.log("[ChatInput] ✅ onAttachmentReady received:", att);

                const frameIds =
                  att.frameIds ||
                  att.frame_ids ||
                  att.frames?.map((f) => f.frame_id || f.id).filter(Boolean) ||
                  [];

                const localFile =
                  typeof File !== "undefined" && att.localFile instanceof File
                    ? att.localFile
                    : null;

                setDraftAttachment({
                  // Fast Chat local draft: preserve the ORIGINAL File object.
                  localFile,
                  file:
                    localFile || {
                      name: att.name || "attachment",
                      type: att.mime || "",
                      size: Number(att.sizeBytes || 0) || 0,
                    },
                  mime: att.mime || localFile?.type || "",
                  fileExtension:
                    att.fileExtension ||
                    att.file_extension ||
                    "",
                  fileTypeLabel:
                    att.fileTypeLabel ||
                    att.file_type_label ||
                    "",
                  sizeBytes:
                    Number.isFinite(Number(att.sizeBytes))
                      ? Number(att.sizeBytes)
                      : Number.isFinite(Number(localFile?.size))
                      ? Number(localFile.size)
                      : null,
                  pendingUpload: !!att.pendingUpload,
                  uploadOnSend:
                    typeof att.uploadOnSend === "function"
                      ? att.uploadOnSend
                      : null,

                  // Existing/legacy metadata remains intact.
                  fileUrl: att.fileUrl || null,
                  fileUrls:
                    att.fileUrls ||
                    (att.fileUrl ? [att.fileUrl] : []),
                  previewUrl: att.previewUrl || null,
                  frameIds: Array.isArray(frameIds) ? frameIds : [],
                  uploadBatchId:
                    att.upload_batch_id ||
                    att.uploadBatchId ||
                    null,

                  // Authoritative DI metadata may be present for reused/processed docs.
                  documentId: att.documentId || att.document_id || null,
                  documentStatus:
                    att.documentStatus ||
                    att.document_status ||
                    null,
                  documentType:
                    att.documentType ||
                    att.document_type ||
                    null,
                  documentTypeConfidence:
                    att.documentTypeConfidence ??
                    att.document_type_confidence ??
                    null,
                  pageCount:
                    att.pageCount ??
                    att.page_count ??
                    null,
                  workerRegion:
                    att.workerRegion ||
                    att.worker_region ||
                    null,
                  manifestPath:
                    att.manifestPath ||
                    att.manifest_path ||
                    null,
                  briefPath:
                    att.briefPath ||
                    att.brief_path ||
                    null,
                  pageMapPath:
                    att.pageMapPath ||
                    att.page_map_path ||
                    null,
                  indexManifestPath:
                    att.indexManifestPath ||
                    att.index_manifest_path ||
                    null,
                  warningCount:
                    att.warningCount ??
                    att.warning_count ??
                    0,

                  mode:
                    att.mode ||
                    (att.previewUrl ? "image" : "file"),
                  progress:
                    typeof att.progress === "number"
                      ? att.progress
                      : att.uploading
                      ? 5
                      : 0,
                });
              }}
            />
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 8,
            }}
          >
            <div style={{ display: "flex", gap: 8, paddingLeft: 4, alignItems: "center" }}>
              <button
                ref={plusButtonRef}
                onPointerDown={() => {
                  setShowModeMenu(false);
                  setShowAttachmentMenu((prev) => !prev);
                }}
                type="button"
                style={iconBtnStyle}
                title="More options"
              >
                <FaPlus />
              </button>

              <button
                ref={modeButtonRef}
                type="button"
                className="asklawly-mode-trigger"
                data-open={showModeMenu ? "true" : "false"}
                aria-haspopup="menu"
                aria-expanded={showModeMenu}
                aria-label={`Response mode: ${selectedMode === "research" ? "Research" : "Auto"}`}
                disabled={isSending || isUploading}
                title={
                  selectedMode === "research"
                    ? "Research mode"
                    : "Auto mode — Lexa decides when research is needed"
                }
                onClick={() => {
                  setShowAttachmentMenu(false);
                  setShowModeMenu((prev) => !prev);
                }}
              >
                <span>{selectedMode === "research" ? "Research" : "Auto"}</span>
                {showModeMenu ? <FiChevronUp size={14} aria-hidden="true" /> : <FiChevronDown size={14} aria-hidden="true" />}
              </button>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", paddingRight: 4 }}>
              {showWave ? (
                <>
                  <button
                    onPointerDown={cancelVoice}
                    type="button"
                    style={iconBtnStyle}
                    title="Cancel voice"
                    disabled={isTranscribing || isAcceptingVoice}
                  >
                    <FaTimes />
                  </button>

                  <button
                    onPointerDown={acceptVoice}
                    type="button"
                    style={iconBtnStyle}
                    title={recording ? "Stop & transcribe" : "Transcribe"}
                    disabled={isTranscribing || isAcceptingVoice || (!recording && !voiceReady)}
                  >
                    {/* ✅ Spinner replaces ✓ while accepting/transcribing (works on iOS/Android/Desktop) */}
                    {isTranscribing || isAcceptingVoice ? <Spinner size={16} /> : <FaCheck />}
                  </button>
                </>
              ) : (
                <button
                  onPointerDown={handleVoiceInput}
                  type="button"
                  style={iconBtnStyle}
                  title="Speak"
                  disabled={finalChatInputDisabled || isUploading || isSending}
                >
                  <FiMic />
                </button>
              )}

              {!showWave && (
                <button
                  onPointerDown={handleSend}
                  type="button"
                  disabled={
                    isUploading ||
                    isSending ||
                    finalChatInputDisabled ||
                    freeDocumentQuotaInsufficient ||
                    (!input.trim() && !draftAttachment)
                  }
                  style={{
                    backgroundColor:
                      input.trim() || draftAttachment
                        ? freeDocumentQuotaInsufficient
                          ? "#cbd5e1"
                          : "#0b5fff"
                        : "#ccc",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: 44,
                    height: 44,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor:
                      (input.trim() || draftAttachment) &&
                      !freeDocumentQuotaInsufficient &&
                      !finalChatInputDisabled &&
                      !isUploading &&
                      !isSending
                        ? "pointer"
                        : "not-allowed",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    boxShadow: isLaunching
                      ? "0 0 0 4px rgba(11, 95, 255, 0.08)"
                      : "none",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  title={
                    freeDocumentQuotaInsufficient
                      ? "Not enough Free usage remaining for document analysis"
                      : "Send"
                  }
                >
                  <FiArrowUp
                    style={{
                      fontSize: 22,
                      color: "#fff",
                      animation: isLaunching
                        ? "asklawlySendLift 1.05s ease-in-out infinite"
                        : "none",
                      opacity: isLaunching ? 0.9 : 1,
                      transition: "opacity 160ms ease",
                      position: "relative",
                      zIndex: 2,
                    }}
                  />
                </button>
              )}
            </div>
          </div>

          {/* Optional tiny hint text while transcribing */}
          {(isTranscribing || isAcceptingVoice) && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#6B7280" }}>Transcribing…</div>
          )}

          {/* Optional last transcript preview (for debugging) */}
          {!!voiceText && !showWave && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#6B7280" }}>
              <span style={{ opacity: 0.9 }}>Last voice:</span> {voiceText}
            </div>
          )}
        </div>

        {showModeMenu && typeof document !== "undefined" &&
          createPortal(
            <div
              ref={modeMenuRef}
              role="menu"
              aria-label="Response mode"
              style={{
                position: "fixed",
                top: modeMenuPosition.top,
                left: modeMenuPosition.left,
                width: 264,
                maxWidth: "calc(100vw - 16px)",
                padding: 6,
                background: "rgba(255,255,255,0.98)",
                border: "1px solid rgba(17,24,39,0.10)",
                borderRadius: 14,
                boxShadow: "0 14px 34px rgba(17,24,39,0.16), 0 2px 8px rgba(17,24,39,0.08)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                zIndex: 2147483000,
                boxSizing: "border-box",
              }}
            >
              {[
                {
                  value: "auto",
                  label: "Auto",
                  description: "Lexa decides when research is needed",
                },
                {
                  value: "research",
                  label: "Research",
                  description: "Use research mode",
                },
              ].map((item) => {
                const active = selectedMode === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    className="asklawly-mode-menu-item"
                    onClick={() => chooseMode(item.value)}
                  >
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        marginTop: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 18px",
                        color: active ? "#111827" : "transparent",
                      }}
                      aria-hidden="true"
                    >
                      <FiCheck size={15} />
                    </span>

                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span
                        style={{
                          display: "block",
                          fontSize: 14,
                          lineHeight: 1.25,
                          fontWeight: active ? 600 : 500,
                          color: "#111827",
                        }}
                      >
                        {item.label}
                      </span>
                      <span
                        style={{
                          display: "block",
                          marginTop: 2,
                          fontSize: 12,
                          lineHeight: 1.3,
                          fontWeight: 400,
                          color: "#6B7280",
                        }}
                      >
                        {item.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>,
            document.body
          )}

        {/* ✅ Disclaimer */}
        <div
          style={{
            width: "100%",
            maxWidth: effectiveMaxWidth,
            marginTop: 6,
            marginBottom: 2,
            display: "flex",
            justifyContent: "center",
            textAlign: "center",
            background: "transparent",
            userSelect: "none",
          }}
        >
          <span
            style={{
              fontSize: 11,
              lineHeight: 1.25,
              color: "#6B7280",
              opacity: 0.95,
              maxWidth: "100%",
              padding: "0 6px",
            }}
          >
            Lexa can make mistakes. Please verify important information.
          </span>
        </div>
      </div>
    </div>
  );
};

const iconBtnStyle = {
  backgroundColor: "#f2f2f2",
  border: "none",
  borderRadius: 8,
  padding: 10,
  cursor: "pointer",
};

export default ChatInput;
